import React, {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import useContext from '../hooks/useContext';

interface IModuleExport<T> {
  default: ComponentType<T>;
}

export interface IComponentList {
  [key: string]: ComponentType<any> | null;
}

interface IPendingComponentList {
  [key: string]: Promise<ComponentType<any>> | undefined;
}

class ComponentLoader {
  private readonly cache: IComponentList;
  private readonly pending: IPendingComponentList;

  public constructor(components?: IComponentList) {
    this.cache = components || {};
    this.pending = {};
  }

  public getComponent(component: string) {
    return this.cache[component];
  }

  public loaded(components: string[]) {
    return components.every((c) => this.cache[c]);
  }

  public async loadAll(components: string[]): Promise<Array<ComponentType<any> | null>> {
    return Promise.all(components.map((c) => this.load(c)));
  }

  public async load(component: string): Promise<ComponentType<any> | null> {
    const pending = this.pending[component];
    if (pending) {
      return pending;
    }

    const loaded = this.getComponent(component);
    if (loaded) {
      return Promise.resolve(loaded);
    }

    const promise = import(
      /* webpackInclude: /\.tsx$/ */
      /* webpackExclude: /\.noimport\.tsx$/ */
      /* webpackChunkName: "components.[request].[index]" */
      /* webpackMode: "lazy" */
      `site/${component}`
    );

    this.pending[component] = promise;

    const type = await promise
      .then((exports: IModuleExport<any>) => {
        const c = exports.default;
        c.displayName = component;
        return c as ComponentType<any>;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });

    this.cache[component] = type;
    return type;
  }
}

let _componentLoader: ComponentLoader;

const componentLoaderAccessor = (components?: IComponentList) => {
  if (!_componentLoader) {
    _componentLoader = new ComponentLoader(components);
  }

  return _componentLoader;
};

const ComponentLoaderContext = createContext<ComponentLoader | undefined>(undefined);

interface IComponentLoaderProvider {
  children: ReactNode;
  components?: IComponentList;
}

export const ComponentLoaderProvider = ({ children, components }: IComponentLoaderProvider) => {
  const componentLoader = useRef(componentLoaderAccessor(components));

  return (
    <ComponentLoaderContext.Provider value={componentLoader.current}>
      {children}
    </ComponentLoaderContext.Provider>
  );
};

export const useComponentLoader = (modules: string[]) => {
  const validModules = modules.filter((m) => !!m);
  const key = modules.sort();
  const componentLoader = useContext(ComponentLoaderContext);
  const [loading, setLoading] = useState(
    validModules.length && !componentLoader.loaded(validModules),
  );

  useEffect(() => {
    if (validModules.length) {
      const loaded = componentLoader.loaded(validModules);
      if (loaded) {
        setLoading(false);
      } else {
        setLoading(true);
        componentLoader.loadAll(validModules).then(() => setLoading(false));
      }
    }
  }, [...key]);

  const get = useCallback((component) => {
    return componentLoader.getComponent(component);
  }, []);

  return {
    get,
    load: componentLoader.load,
    loading,
  };
};

export const usePreloadedComponent = (component: string) => {
  const componentLoader = componentLoaderAccessor();
  return componentLoader.getComponent(component);
};

export default componentLoaderAccessor;
